import { LitElement, html, nothing, type CSSResultGroup } from 'lit';
import { property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { FormAssociatedMixin }      from '../utils/form-associated.mixin.js';
import { FormValidationController, type ValidationConstraints } from '../utils/form-validation.controller.js';
import styles from './styles.js';

export type InputTextVariant    = 'outlined' | 'filled';
export type InputTextSize       = 'small' | 'medium' | 'large';
export type InputTextType       = 'text' | 'password' | 'email' | 'number' | 'tel' | 'url' | 'search';
export type FloatLabelVariant   = 'over' | 'in' | 'on';

/**
 * uwc-inputtext
 *
 * Production-grade text input component with W3C ElementInternals form
 * association and Constraint Validation API support.
 *
 * @attr value          - Current input value.
 * @attr placeholder    - Placeholder text. Ignored when `label` is set, since
 *                        the floating label rests inside the field as the placeholder.
 * @attr type           - Native input type. Default: text.
 * @attr variant        - outlined | filled. Default: outlined.
 * @attr size           - small | medium | large. Default: medium.
 * @attr disabled       - Disable the input.
 * @attr readonly       - Make the input read-only.
 * @attr invalid        - Mark the input as invalid (red border).
 * @attr fluid          - Stretch to fill container width.
 * @attr required       - Mark the field as required.
 * @attr minlength      - Minimum character count.
 * @attr maxlength      - Maximum character count.
 * @attr pattern        - Regex pattern for validation.
 * @attr name           - Input name for form submission.
 * @attr autocomplete   - Native autocomplete attribute.
 * @attr label          - Floating label text. Activates float-label mode when set.
 * @attr float-label    - Float label variant: over (default) | in | on.
 *
 * @slot prefix - Icon/content shown before the input.
 * @slot suffix - Icon/content shown after the input.
 *
 * @fires uwc-input   - Fired on every keystroke. detail: { value }
 * @fires uwc-change  - Fired when value is committed. detail: { value }
 * @fires uwc-focus   - Fired on focus.
 * @fires uwc-blur    - Fired on blur.
 * @fires uwc-invalid - Fired when the field is blurred with an invalid value.
 *                      detail: { validity, validationMessage }
 *
 * @csspart wrapper - The outer wrapper div.
 * @csspart input   - The native <input> element.
 * @csspart label   - The floating label element (only present when label is set).
 *
 * @cssprop --uwc-input-bg                - Background color.
 * @cssprop --uwc-input-border            - Border shorthand.
 * @cssprop --uwc-input-radius            - Border radius.
 * @cssprop --uwc-input-color             - Text color.
 * @cssprop --uwc-input-placeholder-color - Placeholder color.
 * @cssprop --uwc-input-font-size         - Font size.
 * @cssprop --uwc-input-padding-x         - Horizontal padding.
 * @cssprop --uwc-input-padding-y         - Vertical padding.
 */
export class UwcInputText extends FormAssociatedMixin(LitElement) {

  static override styles: CSSResultGroup = [styles];

  // ── Form validation controller ────────────────────────────────────

  private _validation = new FormValidationController(this);

  // ── Core ─────────────────────────────────────────────────────────

  @property() value        = '';
  @property() placeholder?: string;
  @property() type: InputTextType = 'text';
  @property() name?: string;
  @property() autocomplete?: string;

  // ── Validation constraints ────────────────────────────────────────

  @property({ type: Boolean, reflect: true }) required  = false;
  @property({ type: Number }) minlength?: number;
  @property({ type: Number }) maxlength?: number;
  @property() pattern?: string;

  // ── Appearance ────────────────────────────────────────────────────

  @property({ reflect: true }) variant:    InputTextVariant  = 'outlined';
  @property({ reflect: true }) size:       InputTextSize     = 'medium';
  @property() label?:                      string;
  @property({ attribute: 'float-label', reflect: true })
  floatLabel: FloatLabelVariant = 'over';

  // ── State modifiers ───────────────────────────────────────────────

  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean, reflect: true }) readonly = false;
  @property({ type: Boolean, reflect: true }) invalid  = false;
  @property({ type: Boolean, reflect: true }) fluid    = false;

  // ── Slot detection ────────────────────────────────────────────────

  @state() private _hasPrefix = false;
  @state() private _hasSuffix = false;

  // ── Constraints accessor ──────────────────────────────────────────

  private get _constraints(): ValidationConstraints {
    return {
      required:  this.required,
      minlength: this.minlength,
      maxlength: this.maxlength,
      pattern:   this.pattern,
      type:      this.type,
    };
  }

  // ── Lifecycle ─────────────────────────────────────────────────────

  override firstUpdated() {
    this._detectSlots();
    const input = this.shadowRoot?.querySelector('input') ?? null;
    this._validation.setAnchor(input);
    this._validation.syncFormValue(this.value || null);
    // Initialise validity for required-but-empty state on mount
    this._validation.revalidate(this.value, this._constraints);
  }

  override updated(changed: Map<PropertyKey, unknown>) {
    super.updated(changed);
    const constraintKeys: PropertyKey[] = ['required', 'minlength', 'maxlength', 'pattern', 'type'];
    if (constraintKeys.some(k => changed.has(k))) {
      this._validation.revalidate(this.value, this._constraints);
    }
  }

  private _detectSlots() {
    this._hasPrefix = this.querySelector('[slot="prefix"]') !== null;
    this._hasSuffix = this.querySelector('[slot="suffix"]') !== null;
    if (this._hasPrefix) this.setAttribute('has-prefix', '');
    else this.removeAttribute('has-prefix');
    if (this._hasSuffix) this.setAttribute('has-suffix', '');
    else this.removeAttribute('has-suffix');
  }

  // ── Handlers ─────────────────────────────────────────────────────

  private _onInput(e: Event) {
    const input = e.target as HTMLInputElement;
    this.value = input.value;
    this._validation.handleChange(this.value, this._constraints);
    this._validation.syncFormValue(this.value || null);
    this.dispatchEvent(new CustomEvent('uwc-input', {
      bubbles: true, composed: true,
      detail: { value: this.value, originalEvent: e },
    }));
  }

  private _onChange(e: Event) {
    const input = e.target as HTMLInputElement;
    this.value = input.value;
    this._validation.syncFormValue(this.value || null);
    this.dispatchEvent(new CustomEvent('uwc-change', {
      bubbles: true, composed: true,
      detail: { value: this.value, originalEvent: e },
    }));
  }

  private _onBlur() {
    this._validation.setTouched(true);
    this.dispatchEvent(new CustomEvent('uwc-blur', { bubbles: true, composed: true }));
  }

  // ── Render ────────────────────────────────────────────────────────

  override render() {
    const floatMode = !!this.label;
    const classes = classMap({
      'uwc-input': true,
      [`uwc-input--${this.variant}`]: true,
      [`uwc-input--${this.size}`]:    this.size !== 'medium',
      'uwc-input--invalid':   this.invalid,
      'uwc-input--disabled':  this.disabled,
      'uwc-input--float':     floatMode,
      [`uwc-input--float-${this.floatLabel}`]: floatMode,
    });

    // In float-label mode the label rests inside the field and doubles as the
    // placeholder, so any user-supplied placeholder is ignored to avoid it
    // overlapping the resting label. A single space is used instead so
    // :placeholder-shown still drives the CSS float transition.
    const placeholder = floatMode ? ' ' : this.placeholder;

    return html`
      <div part="wrapper" class=${classes}>
        ${this._hasPrefix
          ? html`<span class="uwc-input__prefix"><slot name="prefix"></slot></span>`
          : nothing}
        <input
          part="input"
          type=${this.type}
          .value=${this.value}
          placeholder=${ifDefined(placeholder)}
          name=${ifDefined(this.name)}
          autocomplete=${ifDefined(this.autocomplete)}
          maxlength=${ifDefined(this.maxlength)}
          ?disabled=${this.disabled}
          ?readonly=${this.readonly}
          ?required=${this.required}
          aria-required=${this.required ? 'true' : nothing}
          pattern=${ifDefined(this.pattern)}
          @input=${this._onInput}
          @change=${this._onChange}
          @focus=${() => this.dispatchEvent(new CustomEvent('uwc-focus', { bubbles: true, composed: true }))}
          @blur=${this._onBlur}
        />
        ${floatMode
          ? html`<label class="uwc-input__label" part="label">${this.label}</label>`
          : nothing}
        ${this._hasSuffix
          ? html`<span class="uwc-input__suffix"><slot name="suffix"></slot></span>`
          : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-inputtext': UwcInputText; }
}
